import {
  Component,
  computed,
  input,
  output,
  viewChild,
  SecurityContext,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  TrackerService,
  copyHtmlToClipboard,
} from '@maximizer/outlook/shared/data-access';
import {
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
} from '@maximizer/outlook/shared/domain';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_TOOLTIPS } from '@progress/kendo-angular-tooltip';

let nextHelpTextId = 0;

@Component({
  selector: 'maximizer-outlook-ai-response-actions',
  imports: [TranslatePipe, KENDO_BUTTON, OUTLOOK_SHARED_UI, KENDO_TOOLTIPS],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `<div class="flex justify-between bg-neutral-5 px-2 py-2">
      @if (showFeedback()) {
        <div
          kendoTooltip
          [callout]="false"
          [tooltipTemplate]="tooltipTemplate"
          [title]="'outlook.aiResponseActions.feedback' | translate"
          class="flex gap-1"
        >
          <button
            kendoButton
            themeColor="primary"
            fillMode="flat"
            iconClass="fa-icons icon-thumbs-up icon-16"
            [attr.aria-label]="'outlook.aiResponseActions.helpful' | translate"
            [disabled]="feedBackDisabled() || feedbackGiven()"
            (click)="feedbackVote(true)"
          ></button>
          <button
            kendoButton
            themeColor="primary"
            fillMode="flat"
            iconClass="fa-icons icon-thumbs-down icon-16"
            [attr.aria-label]="
              'outlook.aiResponseActions.notHelpful' | translate
            "
            [disabled]="feedBackDisabled() || feedbackGiven()"
            (click)="feedbackVote(false)"
          ></button>
        </div>
      }

      <div class="flex gap-2">
        @if (showCreateEmail()) {
          <!-- The disabled button leaves the tab order, so the wrapper becomes
               focusable while disabled to keep the reason reachable. -->
          <span
            kendoTooltip
            [callout]="false"
            [tooltipTemplate]="tooltipTemplate"
            [title]="createEmailTitleKey() | translate"
            [attr.tabindex]="createEmailIsDisabled() ? 0 : null"
            [attr.aria-describedby]="
              createEmailIsDisabled() ? createEmailHelpId : null
            "
          >
            @if (createEmailIsDisabled()) {
              <span class="sr-only" [id]="createEmailHelpId">
                {{ createEmailTitleKey() | translate }}
              </span>
            }
            <button
              kendoButton
              themeColor="primary"
              fillMode="flat"
              iconClass="fa-icons icon-envelope icon-16"
              [attr.aria-label]="
                'outlook.aiResponseActions.createEmailAria' | translate
              "
              [attr.aria-describedby]="
                createEmailIsDisabled() ? createEmailHelpId : null
              "
              [disabled]="createEmailIsDisabled()"
              (click)="handleCreateEmail()"
            ></button>
          </span>
        }
        @if (showSaveNote()) {
          <button
            kendoTooltip
            [callout]="false"
            [tooltipTemplate]="tooltipTemplate"
            kendoButton
            themeColor="primary"
            fillMode="flat"
            iconClass="fa-icons icon-file-plus icon-16"
            [attr.aria-label]="'outlook.iqBoost.saveNote' | translate"
            [title]="'outlook.aiResponseActions.saveNote' | translate"
            [disabled]="saveNoteDisabled()"
            (click)="saveNote.emit()"
          ></button>
        }
        <button
          kendoTooltip
          [callout]="false"
          [tooltipTemplate]="tooltipTemplate"
          kendoButton
          themeColor="primary"
          fillMode="flat"
          iconClass="fa-icons icon-copy icon-16"
          [attr.aria-label]="'outlook.summarization.copy' | translate"
          [title]="'outlook.aiResponseActions.copy' | translate"
          [disabled]="copyDisabled() || !copyContent()"
          (click)="handleCopy()"
        ></button>
      </div>
    </div>

    <ng-template #tooltipTemplate let-anchor>
      <p class="caption text-center text-neutral-90 whitespace-nowrap">
        {{ anchor.nativeElement.getAttribute('data-title') }}
      </p>
    </ng-template>

    <maximizer-outlook-notification #notification [closable]="true">
    </maximizer-outlook-notification> `,
})
export class OutlookAiResponseActionsComponent {
  notification =
    viewChild.required<OutlookNotificationComponent>('notification');

  protected readonly createEmailHelpId = `create-email-disabled-help-${nextHelpTextId++}`;

  aiFeature = input<EventModule.AiTimelineSummary | EventModule.IqBoost>(
    EventModule.IqBoost,
  );

  feature = input<string | null>(null);
  showSaveNote = input(false);
  saveNoteDisabled = input(false);
  showFeedback = input(true);
  copyDisabled = input(false);
  feedBackDisabled = input(false);
  feedbackGiven = input(false);
  copyContent = input<string | null>(null);
  copySuccessKey = input('outlook.aiResponseActions.copySuccess');
  showCreateEmail = input(false);
  createEmailDisabled = input(false);
  isComposeMode = input(false);

  saveNote = output<void>();
  feedbackVoted = output<void>();
  createEmail = output<void>();

  createEmailIsDisabled = computed(
    () => this.createEmailDisabled() || !this.copyContent(),
  );

  createEmailTitleKey = computed(() => {
    if (this.createEmailIsDisabled())
      return 'outlook.aiResponseActions.createEmailDisabledHelp';
    return this.isComposeMode()
      ? 'outlook.aiResponseActions.insertReply'
      : 'outlook.aiResponseActions.createEmail';
  });

  constructor(
    private readonly tracker: TrackerService,
    private readonly sanitizer: DomSanitizer,
  ) {}

  handleCopy(): void {
    const content = this.copyContent();
    if (!content) return;

    const feature = this.feature();
    this.tracker.trackEvent({
      module: this.aiFeature(),
      journey: EventJourney.CopyToClipboard,
      action: EventAction.Clicked,
      metadata: {
        ...(feature ? { feature } : {}),
      },
    });

    void copyHtmlToClipboard(
      content,
      (html) => this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '',
    )
      .then(() => {
        this.notification().showMessage('success', this.copySuccessKey(), true);
      })
      .catch(() => {
        this.notification().showMessage(
          'error',
          'outlook.summarization.copyFailed',
          true,
        );
      });
  }

  handleCreateEmail(): void {
    if (this.createEmailIsDisabled()) return;

    this.tracker.trackEvent({
      module: this.aiFeature(),
      journey: EventJourney.DraftEmail,
      action: EventAction.Clicked,
    });

    this.createEmail.emit();
  }

  feedbackVote(helpful: boolean): void {
    const feature = this.feature();
    this.tracker.trackEvent({
      module: this.aiFeature(),
      journey: EventJourney.Feedback,
      action: EventAction.Clicked,
      metadata: {
        type: helpful ? EventMetadata.Positive : EventMetadata.Negative,
        ...(feature ? { feature } : {}),
      },
    });

    this.feedbackVoted.emit();

    this.notification().showMessage(
      'success',
      'outlook.summarization.feedbackSuccess',
      true,
    );
  }
}
